import Image from "next/image";

/**
 * Zoom-on-hover for the logo, matching the photo treatment on directory cards.
 * Full colour at rest: the black-and-white-until-hover effect was removed site
 * wide (CEO, 2026-09-14). Pass as `imageClassName` ONLY on interactive
 * card surfaces (grid / search) — NOT the profile hero or dashboard
 * preview, which show the logo statically in full colour. The resting
 * `scale-100` is load-bearing: Tailwind v4 `scale-*` is the CSS `scale`
 * property, which can't interpolate from `none`, so a base value is required
 * for the transition to animate (see CLAUDE.md Tailwind-v4 scale note).
 */
export const LOGO_REVEAL_CLASS =
  "object-cover motion-safe:transition-[scale] motion-safe:scale-100 motion-safe:duration-700 motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)] motion-safe:group-hover:scale-[1.06]";

/**
 * Company-logo treatment for showcase surfaces (profile hero, search/grid
 * cards, dashboard preview). A logo is shown inside a gold-ringed
 * circle on a navy field — visually distinct from a cover-cropped headshot.
 * This is the single source of truth for that look so it stays identical across
 * every surface; callers pass `className` to match the footprint of the photo
 * they're replacing.
 *
 * Deliberately NOT routed through SkeletonImage: its dot-pattern shimmer assumes
 * a filled rectangle and looks wrong behind a logo on navy. Logos are small and
 * load fast, so a plain next/image is the right call.
 *
 * The image fills the circle with object-cover (per product decision) — it's
 * clipped to the round shape rather than letterboxed; the white circle bg only
 * shows through transparent areas of the source image. Interactive card
 * surfaces pass `imageClassName={LOGO_REVEAL_CLASS}` so the logo gets the same
 * hover zoom as a photo on that card.
 */
export default function ProfileLogoBadge({
  src,
  alt,
  className = "",
  sizes,
  priority = false,
  imageClassName = "object-cover",
}: {
  src: string;
  alt: string;
  /** Footprint classes (sizing / aspect / rounding) to match the photo it replaces. */
  className?: string;
  sizes?: string;
  priority?: boolean;
  /** Classes for the inner logo image. Defaults to a static `object-cover`;
   *  pass `LOGO_REVEAL_CLASS` on interactive cards for the hover zoom. */
  imageClassName?: string;
}) {
  return (
    <div className={`relative flex items-center justify-center bg-navy ${className}`}>
      <div className="relative aspect-square w-[62%] overflow-hidden rounded-full border-2 border-gold bg-white">
        <Image src={src} alt={alt} fill sizes={sizes} priority={priority} className={imageClassName} />
      </div>
    </div>
  );
}
